import Animation from '../ui/Animation.js';
import { typeEffectiveness, stageMultiplier } from '../constants.js';
import { moveDataLoader } from '../data/MoveDataLoader.js';
import { getOSM } from '../OverworldAnimation.js';
export default class Move {
    constructor({
        name,
        type,
        category,
        power,
        accuracy,
        basePP,
        maxPP,
        pp,
        effect,
        statModifier,
        statusEffect,
        volatileStatusEffect,
        priority = 0,
        contact = false,
        minHits = 1,
        maxHits = 1,
        oneHitKO = false,
        recoveryPercentage = 0.0,
        recoilPercentage = 0.0,
        invalidAfterTurn = Number.MAX_SAFE_INTEGER,
        entryHazard,
        id,
        identifier,
        userEffectProbability = null,
        targetEffectProbability = null,
    }) {
        this.name = name;
        this.id = id;
        this.identifier = identifier;
        this.type = type;
        this.category = category;
        this.power = power;
        this.accuracy = accuracy;
        this.basePP = basePP !== null && basePP !== undefined ? basePP : maxPP;
        this.maxPP = maxPP;
        this.pp = pp !== null && pp !== undefined ? pp : maxPP;
        this.effect = effect;
        this.statModifier = statModifier;
        this.statusEffect = statusEffect;
        this.priority = priority;
        this.contact = contact;
        this.minHits = minHits;
        this.maxHits = maxHits;
        this.oneHitKO = oneHitKO;
        this.recoveryPercentage = recoveryPercentage;
        this.recoilPercentage = recoilPercentage;
        this.volatileStatusEffect = volatileStatusEffect;
        this.invalidAfterTurn = invalidAfterTurn;
        this.entryHazard = entryHazard;
        this.userEffectProbability = userEffectProbability;
        this.targetEffectProbability = targetEffectProbability;
    }

    /**
     * Resolve power for this move in a given battle context (e.g. variable power for Flail).
     * When context is provided and implements getMovePower(move, user, target), that override is used; otherwise returns this.power.
     * @param {import('./Pokemon.js').default} [user] - Attacker (for variable-power moves)
     * @param {import('./Pokemon.js').default} [target] - Defender
     * @param {{ getMovePower?: (move: Move, user: import('./Pokemon.js').default, target: import('./Pokemon.js').default) => number | undefined } | null} [context] - Battle context (e.g. BattleStateMachine)
     * @returns {number}
     */
    getPower(user, target, context) {
        if (!context || typeof context.getMovePower !== 'function') return this.power;
        const override = context.getMovePower(this, user, target);
        return override !== undefined && override !== null ? override : this.power;
    }

    /**
     * Resolve accuracy for this move in a given battle context (for future variable-accuracy moves).
     * When context is provided and implements getMoveAccuracy(move, user, target), that override is used; otherwise returns this.accuracy.
     * @param {import('./Pokemon.js').default} [user]
     * @param {import('./Pokemon.js').default} [target]
     * @param {{ getMoveAccuracy?: (move: Move, user: import('./Pokemon.js').default, target: import('./Pokemon.js').default) => number | undefined } | null} [context]
     * @returns {number}
     */
    getAccuracy(user, target, context) {
        if (!context || typeof context.getMoveAccuracy !== 'function') return this.accuracy;
        const override = context.getMoveAccuracy(this, user, target);
        return override !== undefined && override !== null ? override : this.accuracy;
    }

    static hitSelf() {
        const hitSelfData = {
            id: 'hit_self',
            name: 'Hit Self',
            type: 'typeless',
            category: 'physical',
            power: 40,
            accuracy: 0,
            basePP: Number.MAX_SAFE_INTEGER,
            maxPP: Number.MAX_SAFE_INTEGER,
            effect: 'Hits itself in confusion.',
        };
        return new Move(hitSelfData);
    }

    static struggle() {
        try {
            return Move.getById('struggle');
        } catch (_) {
            return new Move({
                id: 'struggle',
                identifier: 'struggle',
                name: 'Struggle',
                type: 'typeless',
                category: 'physical',
                power: 50,
                accuracy: 100,
                basePP: Number.MAX_SAFE_INTEGER,
                maxPP: Number.MAX_SAFE_INTEGER,
                recoilPercentage: 0.25,
            });
        }
    }

    static getById(id) {
        if (id === 'hit_self') {
            return Move.hitSelf();
        }
        const moveData = moveDataLoader.getMove(id);
        if (!moveData && id !== 'hit_self') {
            throw new Error(`Move with id "${id}" not found`);
        }

        // Transform stat modifiers from MoveManager format to Move.js format
        const statModifier = {
            user: {
                stat: {
                    attack: moveData.effect_attack_user || 0,
                    defense: moveData.effect_defense_user || 0,
                    specialAttack: moveData.effect_special_attack_user || 0,
                    specialDefense: moveData.effect_special_defense_user || 0,
                    speed: moveData.effect_speed_user || 0,
                    accuracy: moveData.effect_accuracy_user || 0,
                    evasiveness: moveData.effect_evasiveness_user || 0,
                },
            },
            receiver: {
                stat: {
                    attack: moveData.effect_attack_foe || 0,
                    defense: moveData.effect_defense_foe || 0,
                    specialAttack: moveData.effect_special_attack_foe || 0,
                    specialDefense: moveData.effect_special_defense_foe || 0,
                    speed: moveData.effect_speed_foe || 0,
                    accuracy: moveData.effect_accuracy_foe || 0,
                    evasiveness: moveData.effect_evasiveness_foe || 0,
                },
            },
        };

        // Transform status effects from MoveManager format to Move.js format
        // Use user_effect_probability for user effects, fallback to 0 if null/undefined or no effect
        let userEffectChance = moveData.user_effect_probability ?? 0;
        if (
            moveData.effect_nonvolatile_status_user === null ||
            moveData.effect_nonvolatile_status_user === undefined
        ) {
            userEffectChance = 0;
        }

        // Use target_effect_probability for receiver effects, fallback to 0 if null/undefined or no effect
        let receiverEffectChance = moveData.target_effect_probability ?? 0;
        if (
            moveData.effect_nonvolatile_status_foe === null ||
            moveData.effect_nonvolatile_status_foe === undefined
        ) {
            receiverEffectChance = 0;
        }

        const statusEffect = {
            user: {
                status: moveData.effect_nonvolatile_status_user || null,
                chance: userEffectChance,
            },
            receiver: {
                status: moveData.effect_nonvolatile_status_foe || null,
                chance: receiverEffectChance,
            },
        };

        // Use user_effect_probability for volatile user effects
        userEffectChance = moveData.user_effect_probability ?? 0;
        if (
            moveData.effect_volatile_status_user === null ||
            moveData.effect_volatile_status_user === undefined
        ) {
            userEffectChance = 0;
        }

        // Use target_effect_probability for volatile receiver effects
        receiverEffectChance = moveData.target_effect_probability ?? 0;
        if (
            moveData.effect_volatile_status_foe === null ||
            moveData.effect_volatile_status_foe === undefined
        ) {
            receiverEffectChance = 0;
        }

        // Transform volatile status effects from MoveManager format to Move.js format
        const volatileStatusEffect = {
            user: {
                status: moveData.effect_volatile_status_user || null,
                chance: userEffectChance,
            },
            receiver: {
                status: moveData.effect_volatile_status_foe || null,
                chance: receiverEffectChance,
            },
        };

        return new Move({
            id: moveData.id,
            identifier: moveData.identifier,
            name: moveData.name,
            type: moveData.type,
            power: moveData.power,
            accuracy: moveData.accuracy,
            basePP: moveData.maxPP,
            maxPP: moveData.maxPP,
            pp: moveData.maxPP,
            effect: moveData.effect,
            category: moveData.category,
            statModifier: statModifier,
            statusEffect: statusEffect,
            priority: moveData.priority,
            contact: moveData.contact,
            minHits: moveData.min_hits,
            maxHits: moveData.max_hits,
            oneHitKO: moveData.one_hit_ko,
            recoveryPercentage: moveData.recovery_percentage,
            recoilPercentage: moveData.recoil_percentage,
            volatileStatusEffect: volatileStatusEffect,
            invalidAfterTurn: Number.MAX_SAFE_INTEGER, // Not in MoveManager, use default
            entryHazard: moveData.entry_hazard,
            userEffectProbability: moveData.user_effect_probability ?? null,
            targetEffectProbability: moveData.target_effect_probability ?? null,
        });
    }

    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        return {
            id: this.id,
            name: this.name,
            type: this.type,
            category: this.category,
            power: this.power,
            accuracy: this.accuracy,
            basePP: this.basePP,
            basePP: this.basePP,
            maxPP: this.maxPP,
            pp: this.pp,
            effect: this.effect,
            statModifier: this.statModifier,
            statusEffect: this.statusEffect,
            priority: this.priority,
            contact: this.contact,
            minHits: this.minHits,
            maxHits: this.maxHits,
            oneHitKO: this.oneHitKO,
            recoveryPercentage: this.recoveryPercentage,
            recoilPercentage: this.recoilPercentage,
            volatileStatusEffect: this.volatileStatusEffect,
            invalidAfterTurn: this.invalidAfterTurn,
        };
    }

    static deserialize(data) {
        if (data === null || data === undefined) {
            return null;
        }

        if (data.identifier) {
            const deserializedMove = Move.getById(data.identifier);
            if (data.basePP !== null && data.basePP !== undefined) deserializedMove.basePP = data.basePP;
            if (data.maxPP !== null && data.maxPP !== undefined) deserializedMove.maxPP = data.maxPP;
            if (data.pp !== null && data.pp !== undefined) deserializedMove.pp = data.pp;
            return deserializedMove;
        }

        return new Move({
            name: data.name,
            type: data.type,
            category: data.category,
            power: data.power,
            accuracy: data.accuracy,
            basePP: data.basePP ?? data.maxPP,
            maxPP: data.maxPP,
            pp: data.pp,
            effect: data.effect,
            statModifier: data.statModifier,
            statusEffect: data.statusEffect,
            priority: data.priority,
            contact: data.contact,
            minHits: data.minHits,
            maxHits: data.maxHits,
            oneHitKO: data.oneHitKO,
            recoveryPercentage: data.recoveryPercentage,
            recoilPercentage: data.recoilPercentage,
            volatileStatusEffect: data.volatileStatusEffect,
            invalidAfterTurn: data.invalidAfterTurn,
            userEffectProbability: data.userEffectProbability ?? null,
            targetEffectProbability: data.targetEffectProbability ?? null,
        });
    }

    hasNonZeroStat() {
        for (const stat in this.statModifier.user.stat) {
            if (this.statModifier.user.stat[stat] !== 0) {
                return true;
            }
        }
        for (const stat in this.statModifier.receiver.stat) {
            if (this.statModifier.receiver.stat[stat] !== 0) {
                return true;
            }
        }

        return false;
    }

    hasUserStatModifiers() {
        for (const stat in this.statModifier.user.stat) {
            if (this.statModifier.user.stat[stat] !== 0) {
                return true;
            }
        }
        return false;
    }

    hasReceiverStatModifiers() {
        for (const stat in this.statModifier.receiver.stat) {
            if (this.statModifier.receiver.stat[stat] !== 0) {
                return true;
            }
        }
        return false;
    }

    getStatusVerb(status) {
        switch (status) {
            case 'paralysis':
                return 'was paralized';
            case 'poison':
                return 'was poisoned';
            case 'burn':
                return 'was burned';
            case 'freeze':
                return 'froze';
            case 'sleep':
                return 'fell asleep';
        }
    }

    hasPrimaryStatusEffect() {
        if (this.statusEffect === undefined || this.statusEffect === null) {
            return false;
        }
        return !!this.statusEffect.user.status || !!this.statusEffect.receiver.status;
    }

    hasVolatileStatusEffect() {
        if (this.volatileStatusEffect === null || this.volatileStatusEffect === undefined) {
            return false;
        }
        return !!this.volatileStatusEffect.user.status || !!this.volatileStatusEffect.receiver.status;
    }

    hasSecondaryStatusEffect() {
        // TODO: Finish implementation
        return !!this.statusEffect.user.status || !!this.statusEffect.receiver.status;
    }

    getStatModificationResult(attacker, defender) {
        const result = { attackerStats: [], defenderStats: [] };
        for (const stat in this.statModifier.user.stat) {
            if (this.statModifier.user.stat[stat] !== 0) {
                let attackerStatAmount = this.statModifier.user.stat[stat];
                const currentAttackerStage = attacker.battleStages[stat];

                switch (currentAttackerStage) {
                    case -6:
                        if (attackerStatAmount === -1 || attackerStatAmount === -2) {
                            attackerStatAmount = 0;
                        }
                        break;
                    case -5:
                        if (attackerStatAmount === -2) {
                            attackerStatAmount = -1;
                        }
                        break;
                    case 5:
                        if (attackerStatAmount === 2) {
                            attackerStatAmount = 1;
                        }
                        break;
                    case 6:
                        if (attackerStatAmount === 1 || attackerStatAmount === 2) {
                            attackerStatAmount = 0;
                        }
                        break;
                    default:
                        break;
                }

                result['effect'] = 0;
                if (attackerStatAmount !== 0) {
                    result['pokemon'] = attacker;
                    result['attackerStats'].push(stat);
                    result['effect'] = attackerStatAmount > 0 ? 1 : -1;
                }
            }
        }

        for (const stat in this.statModifier.receiver.stat) {
            if (this.statModifier.receiver.stat[stat] !== 0) {
                let defenderStatAmount = this.statModifier.receiver.stat[stat];
                const currentDefenderStage = defender.battleStages[stat];

                switch (currentDefenderStage) {
                    case -6:
                        if (defenderStatAmount === -1 || defenderStatAmount === -2) {
                            defenderStatAmount = 0;
                        }
                        break;
                    case -5:
                        if (defenderStatAmount === -2) {
                            defenderStatAmount = -1;
                        }
                        break;
                    case 5:
                        if (defenderStatAmount === 2) {
                            defenderStatAmount = 1;
                        }
                        break;
                    case 6:
                        if (defenderStatAmount === 1 || defenderStatAmount === 2) {
                            defenderStatAmount = 0;
                        }
                        break;
                    default:
                        break;
                }

                if (defenderStatAmount !== 0) {
                    result['pokemon'] = defender;
                    result['defenderStats'].push(stat);
                    result['effect'] = defenderStatAmount > 0 ? 1 : -1;
                } else {
                    result['effect'] = 0;
                }
            }
        }
        return result;
    }

    getStatModificationText(attacker, defender) {
        const getStatDisplayName = (stat) => {
            switch (stat) {
                case 'attack':
                    return 'Attack';
                case 'defense':
                    return 'Defense';
                case 'specialAttack':
                    return 'Special Attack';
                case 'specialDefense':
                    return 'Special Defense';
                case 'speed':
                    return 'Speed';
                case 'accuracy':
                    return 'Accuracy';
                case 'evasiveness':
                    return 'Evasiveness';
            }
        };
        const textEvents = [];
        for (const stat in this.statModifier.user.stat) {
            if (this.statModifier.user.stat[stat] !== 0) {
                let attackerStatAmount = this.statModifier.user.stat[stat];
                const currentAttackerStage = attacker.battleStages[stat];

                switch (currentAttackerStage) {
                    case -6:
                        if (attackerStatAmount === -1 || attackerStatAmount === -2) {
                            attackerStatAmount = 0;
                        }
                        break;
                    case -5:
                        if (attackerStatAmount === -2) {
                            attackerStatAmount = -1;
                        }
                        break;
                    case 5:
                        if (attackerStatAmount === 2) {
                            attackerStatAmount = 1;
                        }
                        break;
                    case 6:
                        if (attackerStatAmount === 1 || attackerStatAmount === 2) {
                            attackerStatAmount = 0;
                        }
                        break;
                    default:
                        break;
                }

                if (attackerStatAmount !== 0) {
                    textEvents.push(
                        `${attacker.name}'s ${getStatDisplayName(stat)} ${
                            Math.abs(attackerStatAmount) === 2 ? 'sharply ' : ''
                        } ${attackerStatAmount > 0 ? 'rose' : 'fell'}!`
                    );
                } else {
                    textEvents.push(
                        `${attacker.name}'s ${getStatDisplayName(stat)} won't go any ${
                            currentAttackerStage > 0 ? 'higher' : 'lower'
                        }!`
                    );
                }
            }
        }

        for (const stat in this.statModifier.receiver.stat) {
            if (this.statModifier.receiver.stat[stat] !== 0) {
                let defenderStatAmount = this.statModifier.receiver.stat[stat];
                const currentDefenderStage = defender.battleStages[stat];

                switch (currentDefenderStage) {
                    case -6:
                        if (defenderStatAmount === -1 || defenderStatAmount === -2) {
                            defenderStatAmount = 0;
                        }
                        break;
                    case -5:
                        if (defenderStatAmount === -2) {
                            defenderStatAmount = -1;
                        }
                        break;
                    case 5:
                        if (defenderStatAmount === 2) {
                            defenderStatAmount = 1;
                        }
                        break;
                    case 6:
                        if (defenderStatAmount === 1 || defenderStatAmount === 2) {
                            defenderStatAmount = 0;
                        }
                        break;
                    default:
                        break;
                }

                if (defenderStatAmount !== 0) {
                    textEvents.push(
                        `${defender.name}'s ${getStatDisplayName(stat)} ${
                            Math.abs(defenderStatAmount) === 2 ? 'sharply ' : ''
                        }${defenderStatAmount > 0 ? 'rose' : 'fell'}!`
                    );
                } else {
                    textEvents.push(
                        `${defender.name}'s ${getStatDisplayName(stat)} won't go any ${
                            currentDefenderStage > 0 ? 'higher' : 'lower'
                        }!`
                    );
                }
            }
        }
        return textEvents;
    }

    getAnimation(attacker, defender, playerPokemon) {
        // TODO: Build database of move animations to read from.
        let animation = [[]];

        if (['whirlwind', 'roar'].includes(this.id)) {
            if (playerPokemon === attacker) {
                animation = [
                    [
                        {
                            animationFunction: Animation.moveRight,
                            speed: 2,
                            duration: 40,
                            pokemon: defender,
                        },
                        {
                            animationFunction: Animation.hide,
                            speed: 1,
                            duration: 10,
                            pokemon: defender,
                        },
                        {
                            animationFunction: Animation.moveLeft,
                            speed: 80,
                            duration: 1,
                            pokemon: defender,
                        },
                    ],
                ];
            } else {
                animation = [
                    [
                        {
                            animationFunction: Animation.moveLeft,
                            speed: 2,
                            duration: 40,
                            pokemon: defender,
                        },
                        {
                            animationFunction: Animation.hide,
                            speed: 1,
                            duration: 10,
                            pokemon: defender,
                        },
                        {
                            animationFunction: Animation.moveRight,
                            speed: 80,
                            duration: 1,
                            pokemon: defender,
                        },
                    ],
                ];
            }
        }

        if (this.category === 'status') {
            return animation;
        }

        if (playerPokemon === attacker) {
            // The player is attacking, animation goes from left to right.
            animation = [
                [
                    {
                        animationFunction: Animation.moveRight,
                        speed: 2,
                        duration: 8,
                        pokemon: attacker,
                    },
                    {
                        animationFunction: Animation.moveLeft,
                        speed: 2,
                        duration: 8,
                        pokemon: attacker,
                    },
                ],
                [
                    {
                        animationFunction: Animation.moveRight,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    {
                        animationFunction: Animation.moveLeft,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    {
                        animationFunction: Animation.moveRight,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    {
                        animationFunction: Animation.moveLeft,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    // Rotation Example
                    // {
                    //     animationFunction: Animation.rotate,
                    //     speed: 4,
                    //     duration: 360 / 4,
                    //     pokemon: defender,
                    // },
                    // {
                    //     animationFunction: Animation.resetRotation,
                    //     speed: 1,
                    //     duration: 1,
                    //     pokemon: defender,
                    // },
                ],
                [
                    {
                        animationFunction: Animation.wait,
                        speed: 1,
                        duration: 20,
                        pokemon: attacker,
                    },
                ],
            ];
        } else {
            // The player is defending, animation goes from right to left.
            animation = [
                [
                    {
                        animationFunction: Animation.moveLeft,
                        speed: 2,
                        duration: 8,
                        pokemon: attacker,
                    },
                    {
                        animationFunction: Animation.moveRight,
                        speed: 2,
                        duration: 8,
                        pokemon: attacker,
                    },
                ],
                [
                    {
                        animationFunction: Animation.moveLeft,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    {
                        animationFunction: Animation.moveRight,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    {
                        animationFunction: Animation.moveLeft,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                    {
                        animationFunction: Animation.moveRight,
                        speed: 1,
                        duration: 2,
                        pokemon: defender,
                    },
                ],
                [
                    {
                        animationFunction: Animation.wait,
                        speed: 1,
                        duration: 20,
                        pokemon: attacker,
                    },
                ],
            ];
        }
        return animation;
    }

    getTypeEffectiveness(pokemon) {
        const effect1 = typeEffectiveness[this.type][pokemon.type1] || 1;
        const effect2 = typeEffectiveness[this.type][pokemon.type2] || 1;

        // If both types are the same, don't double count the effect
        if (pokemon.type1 === pokemon.type2) {
            return effect1;
        }

        return effect1 * effect2;
    }

    getEntryHazard() {
        if (this.entryHazard !== null && this.entryHazard !== undefined) {
            return this.entryHazard;
        }
        return null;
    }

    /** Max PP cap from PP Up/PP Max: 8/5 of base PP. */
    getMaxPPCap() {
        return Math.floor((this.basePP * 8) / 5);
    }

    canApplyItem(item) {
        if (['ether', 'max_ether', 'elixir', 'max_elixir'].includes(item.id)) {
            return this.pp < this.maxPP;
        }
        if (item.id === 'pp_up') {
            return this.maxPP < this.getMaxPPCap();
        }
        if (item.id === 'pp_max') {
            return this.maxPP < this.getMaxPPCap();
        }
    }

    applyItem(item_id) {
        switch (item_id) {
            case 'ether':
                this.pp += 10;
                if (this.pp > this.maxPP) {
                    this.pp = this.maxPP;
                }
                break;
            case 'max_ether':
                this.pp = this.maxPP;
                break;
            case 'elixir':
                this.pp += 10;
                if (this.pp > this.maxPP) {
                    this.pp = this.maxPP;
                }
                break;
            case 'max_elixir':
                this.pp = this.maxPP;
                break;
            case 'pp_up': {
                const cap = this.getMaxPPCap();
                const increase = Math.floor(this.basePP / 5);
                const actualIncrease = Math.min(increase, cap - this.maxPP);
                if (actualIncrease > 0) {
                    this.maxPP += actualIncrease;
                    this.pp = Math.min(this.pp + actualIncrease, this.maxPP);
                }
                break;
            }
            case 'pp_max': {
                const cap = this.getMaxPPCap();
                const oldMaxPP = this.maxPP;
                this.maxPP = cap;
                const increase = this.maxPP - oldMaxPP;
                if (increase > 0) {
                    this.pp = Math.min(this.pp + increase, this.maxPP);
                }
                break;
            }
        }
    }

    isPhysical() {
        return this.category === 'physical';
    }

    isSpecial() {
        return this.category === 'special';
    }

    isStatus() {
        return this.category === 'status';
    }

    calculateEffectiveness(target) {
        if (this.type === 'typeless') {
            return 1;
        }
        const effect1 = typeEffectiveness[this.type][target.type1] || 1;
        const effect2 = typeEffectiveness[this.type][target.type2] || 1;

        if (target.type1 === target.type2) {
            return effect1;
        }

        return effect1 * effect2;
    }

    /**
     * @param {object} [options] - Optional battle modifiers
     * @param {number} [options.effectivePower] - Override base power (e.g. for Mud Sport / Water Sport)
     */
    calculateDamage(user, target, options = {}) {
        // Is One-Hit KO Move
        if (this.oneHitKO) {
            const damageDealt = target.stats.hp;
            const recoilDamage = 0;
            const hpRecovered = 0;

            return {
                result: damageDealt,
                summary: {
                    damageDealt,
                    isCritical: false,
                    randomFactor: 1.0,
                    typeFactor: 1.0,
                    recoilDamage,
                    hpRecovered,
                },
                valueOf: () => damageDealt,
                toString: () => damageDealt.toString(),
            };
        }

        // Is Critical
        const criticalHitStages = {
            0: 1 / 16,
            1: 1 / 8,
            2: 1 / 4,
            3: 1 / 3,
            4: 1 / 2,
        };
        const isCritical = Math.random(1) < criticalHitStages[user.getCriticalHitStage()];
        const critical = isCritical ? 2 : 1;

        // Physical Stats
        const userAttackStat = user.stats.attack * stageMultiplier.general[user.battleStages.attack];
        const targetDefenseStat = target.stats.defense * stageMultiplier.general[target.battleStages.defense];

        // Special Stats
        const userSpAttackStat =
            user.stats.specialAttack * stageMultiplier.general[user.battleStages.specialAttack];
        const targetSpDefenseStat =
            target.stats.specialDefense * stageMultiplier.general[target.battleStages.specialDefense];

        // Range randomness (0.85–1.0); Always High Roll cheat forces 1.0
        const alwaysHighRoll = getOSM()?.optionsMenu?.battleCheatAlwaysHighRoll === 'on';
        const randomFactor = alwaysHighRoll ? 1.0 : Math.random() * (1 - 0.85) + 0.85;

        const typeFactor = this.calculateEffectiveness(target);
        const stab =
            this.type === user.type1 || this.type === user.type2
                ? user.ability?.identifier === 'adaptability'
                    ? 2
                    : 1.5
                : 1;
        let burnFactor = user.isBurned() && this.isPhysical() ? 0.5 : 1;

        const abilityMultiplier = {
            attack: 1,
            defense: 1,
            specialAttack: 1,
            specialDefense: 1,
        };
        switch (user.ability?.identifier) {
            case 'guts':
                if (user.isBurned()) {
                    burnFactor = 1;
                }
                if (user.isStatused()) {
                    abilityMultiplier.attack = 1.5;
                }
                break;
            case 'torrent':
                if (user.isPinched() && this.type === 'water') {
                    abilityMultiplier.specialAttack = 1.5;
                    abilityMultiplier.attack = 1.5;
                }
                break;
            case 'blaze':
                if (user.isBurned() && this.type === 'fire') {
                    abilityMultiplier.attack = 1.5;
                    abilityMultiplier.specialAttack = 1.5;
                }
                break;
            case 'overgrow':
                if (user.isPinched() && this.type === 'grass') {
                    abilityMultiplier.attack = 1.5;
                    abilityMultiplier.specialAttack = 1.5;
                }
                break;
            case 'hustle':
                // Hustle increases Attack by 50% for physical moves only
                // One-hit KO moves and special moves are not affected
                if (this.isPhysical() && !this.oneHitKO) {
                    abilityMultiplier.attack = 1.5;
                }
                break;
        }

        let attackStat = userAttackStat;
        let defenseStat = targetDefenseStat;
        if (this.isSpecial()) {
            attackStat = userSpAttackStat;
            defenseStat = targetSpDefenseStat;
        }
        attackStat *= abilityMultiplier.attack;
        defenseStat *= abilityMultiplier.defense;

        const power = options.effectivePower !== undefined ? options.effectivePower : this.power;
        const damageDealt = Math.ceil(
            (((((2 * user.level) / 5 + 2) * power * (attackStat / defenseStat)) / 50) * burnFactor + 2) *
                critical *
                randomFactor *
                typeFactor *
                stab
        );

        const recoilDamage = Math.ceil(damageDealt * this.recoilPercentage);
        const hpRecovered = Math.ceil(damageDealt * this.recoveryPercentage);

        return {
            result: damageDealt,
            summary: {
                damageDealt,
                isCritical,
                randomFactor,
                typeFactor,
                recoilDamage,
                hpRecovered,
            },
            valueOf: () => damageDealt,
            toString: () => damageDealt.toString(),
        };
    }

    makesContact() {
        if (this.contact === null || this.contact === undefined) {
            return false;
        }
        if (typeof this.contact !== 'boolean') {
            return false;
        }
        return this.contact;
    }
}
